import { Component } from 'react';

/**
 * Production error boundary — prevents a single render crash from white-screening the SPA.
 * Use at app root, per-route, and around heavy widgets (charts).
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    try {
      // eslint-disable-next-line no-console
      console.error('[TMR ErrorBoundary]', this.props.name || 'app', error, info?.componentStack);
    } catch (_) { /* noop */ }
    if (typeof this.props.onError === 'function') {
      try { this.props.onError(error, info); } catch (_) { /* noop */ }
    }
  }

  reset = () => {
    this.setState({ error: null, info: null });
    if (typeof this.props.onReset === 'function') {
      try { this.props.onReset(); } catch (_) { /* noop */ }
    }
  };

  render() {
    const { error } = this.state;
    const { children, fallback, name = 'section' } = this.props;

    if (error) {
      if (typeof fallback === 'function') {
        return fallback({ error, reset: this.reset, name });
      }
      if (fallback) return fallback;

      return (
        <div
          className="tmr-error-fallback"
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            minHeight: 160,
            padding: 24,
            margin: 8,
            borderRadius: 12,
            border: '1px solid var(--border-color, rgba(255,255,255,0.12))',
            background: 'var(--bg-card, rgba(18,18,22,0.85))',
            color: 'var(--text-main, #fff)',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--red, #ff0055)' }}>
            {name} failed to render
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted, rgba(255,255,255,0.7))', maxWidth: 480, lineHeight: 1.45 }}>
            Something went wrong in this panel. The rest of the dashboard should still work.
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-dim, rgba(255,255,255,0.5))', fontFamily: 'var(--font-mono, monospace)', maxWidth: 520, wordBreak: 'break-word' }}>
            {String(error?.message || error || 'Unknown error')}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={this.reset}
              style={{
                background: 'var(--btn-bg, #00F0FF)',
                color: 'var(--btn-text, #050506)',
                border: 'none',
                borderRadius: 8,
                padding: '8px 16px',
                fontWeight: 800,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => { try { window.location.reload(); } catch (_) {} }}
              style={{
                background: 'transparent',
                color: 'var(--text-main, #fff)',
                border: '1px solid var(--border-color, rgba(255,255,255,0.2))',
                borderRadius: 8,
                padding: '8px 16px',
                fontWeight: 700,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    return children;
  }
}
